import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, Mail } from "lucide-react";
import { useLang } from "../context/LangContext";
import { api } from "../lib/api";

export const Newsletter = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/subscribers", { email });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || t("news_error"));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(result.coupon);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section data-testid="newsletter-section" className="bg-brand-primary text-canvas">
      <div className="max-w-4xl mx-auto px-6 py-24 md:py-28 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="overline text-brand-secondary mb-4">{t("news_overline")}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">{t("news_title")}</h2>
          <p className="text-canvas/80 max-w-lg mx-auto mb-10">{t("news_sub")}</p>

          {result ? (
            <div data-testid="newsletter-success" className="max-w-md mx-auto">
              <p className="text-canvas/90 mb-4">{result.already ? t("news_already") : t("news_success")}</p>
              <div className="flex items-center justify-center gap-3 bg-canvas text-ink px-6 py-4">
                <span data-testid="coupon-code" className="font-heading text-3xl tracking-wide">{result.coupon}</span>
                <button
                  data-testid="copy-coupon-btn"
                  onClick={copyCode}
                  className="ml-2 flex items-center gap-1 text-sm text-brand-primary hover:text-brand-dark transition-colors"
                >
                  {copied ? <><Check size={16} /> {t("news_copied")}</> : <><Copy size={16} /> {t("news_copy")}</>}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex items-center gap-2 bg-canvas/10 border border-canvas/30 px-4">
                <Mail size={18} className="text-canvas/70 shrink-0" />
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("news_placeholder")}
                  className="flex-1 bg-transparent py-3 text-canvas placeholder:text-canvas/50 focus:outline-none"
                />
              </div>
              <button
                data-testid="newsletter-submit-btn"
                type="submit"
                disabled={loading}
                className="bg-canvas text-brand-primary px-8 py-3 text-sm uppercase tracking-wide hover:bg-canvas-deep transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {loading ? "..." : t("news_btn")}
              </button>
            </form>
          )}
          {error && <p data-testid="newsletter-error" className="text-canvas mt-4 text-sm bg-brand-dark/40 inline-block px-4 py-2">{error}</p>}
        </motion.div>
      </div>
    </section>
  );
};
